/**
 * wayai create [folder] [--label <l>] [-y] — explicitly create a NEW hub from a
 * from-scratch (idless) folder, then push its initial config.
 *
 * This is the discoverable verb for "make a new hub" (gh #3117). `wayai push`
 * still auto-creates when it unambiguously resolves to a single new folder, but
 * `create` de-overloads the intent: it targets ONLY idless folders, so a selector
 * that names an already-created hub is an explicit error (→ `wayai push`), never a
 * silent re-target.
 *
 * Target resolution (idless folders only):
 *   - `<folder>` positional → that new folder (an existing hub name → error)
 *   - cwd inside a new (idless) folder → that folder
 *   - exactly one new folder in the hubs dir → it
 */

import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { createAndPushNewHub } from './push.js';
import {
  resolveWorkspaceDir,
  scanWorkspaceHubs,
  scanNewHubs,
  resolveNewHubForCreate,
  findGitRoot,
} from '../lib/workspace.js';
import { warnLayoutOnce, hubsDirLabel } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { assertScopeAllowsCreation } from '../lib/worktree-scope.js';
import * as fs from 'node:fs';

interface ParsedArgs {
  autoConfirm: boolean;
  folderSelector?: string;
  label?: string;
}

function parseArgs(args: string[]): ParsedArgs {
  let autoConfirm = false;
  let folderSelector: string | undefined;
  let label: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--yes' || arg === '-y') {
      autoConfirm = true;
    } else if (arg === '--label' && args[i + 1]) {
      label = args[++i];
    } else if (!arg.startsWith('-') && folderSelector === undefined) {
      folderSelector = arg;
    }
  }

  return { autoConfirm, folderSelector, label };
}

export async function createCommand(args: string[]): Promise<void> {
  const { autoConfirm, folderSelector, label } = parseArgs(args);
  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const workspaceDir = resolveWorkspaceDir();
  const gitRoot = findGitRoot();
  const wsLabel = hubsDirLabel(gitRoot);
  if (gitRoot) warnLayoutOnce(gitRoot);
  if (!fs.existsSync(workspaceDir)) {
    console.error(`No ${wsLabel}/ directory found. Create hub files in ${wsLabel}/<hub>/hub.yaml with \`hub: { name: ... }\` first.`);
    process.exit(1);
  }

  const existingHubs = scanWorkspaceHubs(workspaceDir);
  const newHubs = scanNewHubs(workspaceDir);

  const resolution = resolveNewHubForCreate(workspaceDir, existingHubs, newHubs, folderSelector);
  if (!resolution.ok) {
    if (resolution.reason === 'exists') {
      const folder = path.basename(resolution.existing.hubFolder);
      console.error(`Hub "${folder}" already exists (${resolution.existing.hubId}). Use \`wayai push --hub ${folder}\` to update it.`);
      process.exit(1);
    }
    if (resolution.reason === 'selector_miss') {
      console.error(`No new (uncreated) hub folder "${folderSelector}" found in ${wsLabel}/. A new hub folder needs a hub.yaml with \`hub: { name: ... }\` and no hub_id.`);
      process.exit(1);
    }
    if (resolution.reason === 'none') {
      console.error(`No new (uncreated) hub folders found in ${wsLabel}/. Create ${wsLabel}/<hub>/hub.yaml with \`hub: { name: ... }\` first, then run \`wayai create\`.`);
      process.exit(1);
    }
    // ambiguous
    console.error(`Multiple new hub folders found in ${wsLabel}/. Pass the folder name (\`wayai create <folder>\`) or run from inside one:`);
    for (const h of newHubs) {
      console.error(`  ${path.basename(h.hubFolder)} (new — "${h.hubName}")`);
    }
    process.exit(1);
  }

  // Refuse creating a hub inside a worktree already scoped to other hubs.
  assertScopeAllowsCreation('hubs', resolution.hub.hubName);
  await createAndPushNewHub(client, resolution.hub, { autoConfirm, label, organizationId });
}
